import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EditorModule } from '@tinymce/tinymce-angular';
import { PageComponent } from '../../../../../services/website.service';

@Component({
  selector: 'app-text-component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    EditorModule
  ],
  templateUrl: './text-component.component.html',
  styleUrls: ['./text-component.component.scss']
})
export class TextComponentComponent {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() editorConfig: any;
  @Input() isSelected: boolean = false;
  @Output() contentChange = new EventEmitter<string>();
  @Output() styleChange = new EventEmitter<void>();

  backgroundTypes = [
    { value: 'solid', label: 'Vaste Kleur' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'image', label: 'Afbeelding' }
  ];

  titlePositions = [
    { value: 'left', label: 'Links' },
    { value: 'center', label: 'Gecentreerd' },
    { value: 'right', label: 'Rechts' }
  ];

  getContent(): string {
    return this.component.data['content'] || '';
  }

  getPreviewContent(): string {
    const content = this.getContent();
    return content || '<p class="text-gray-400 italic">Klik hier om tekst toe te voegen</p>';
  }

  onContentChange(content: string) {
    this.component.data['content'] = content;
    this.contentChange.emit(content);
  }

  onStyleChange() {
    this.styleChange.emit();
  }

  // Style getters
  getHeading(): string {
    return this.component.data['heading'] || '';
  }

  getBackgroundType(): string {
    return this.component.data['backgroundType'] || 'solid';
  }

  getBackgroundColor(): string {
    return this.component.data['backgroundColor'] || '#ffffff';
  }

  getGradientFrom(): string {
    return this.component.data['gradientFrom'] || '#ffffff';
  }

  getGradientTo(): string {
    return this.component.data['gradientTo'] || '#ffffff';
  }

  getBackgroundImage(): string {
    return this.component.data['backgroundImage'] || '';
  }

  getTitlePosition(): string {
    return this.component.data['titlePosition'] || 'left';
  }

  getHeadingColor(): string {
    return this.component.data['headingColor'] || '#111827';
  }

  getTextColor(): string {
    return this.component.data['textColor'] || '#374151';
  }

  getParallax(): boolean {
    return this.component.data['parallax'] || false;
  }

  getSectionHeight(): string {
    return this.component.data['sectionHeight'] || '';
  }

  getHeightUnit(): string {
    return this.component.data['heightUnit'] || 'px';
  }

  // Style setters
  setHeading(value: string) {
    this.component.data['heading'] = value;
    this.onStyleChange();
  }

  setBackgroundType(value: string) {
    this.component.data['backgroundType'] = value;
    this.onStyleChange();
  }

  setBackgroundColor(value: string) {
    this.component.data['backgroundColor'] = value;
    this.onStyleChange();
  }

  setGradientFrom(value: string) {
    this.component.data['gradientFrom'] = value;
    this.onStyleChange();
  }

  setGradientTo(value: string) {
    this.component.data['gradientTo'] = value;
    this.onStyleChange();
  }

  setBackgroundImage(value: string) {
    this.component.data['backgroundImage'] = value;
    this.onStyleChange();
  }

  setTitlePosition(value: string) {
    this.component.data['titlePosition'] = value;
    this.onStyleChange();
  }

  setHeadingColor(value: string) {
    this.component.data['headingColor'] = value;
    this.onStyleChange();
  }

  setTextColor(value: string) {
    this.component.data['textColor'] = value;
    this.onStyleChange();
  }

  setParallax(value: boolean) {
    this.component.data['parallax'] = value;
    this.onStyleChange();
  }

  setSectionHeight(value: string) {
    this.component.data['sectionHeight'] = value;
    this.onStyleChange();
  }

  setHeightUnit(value: string) {
    this.component.data['heightUnit'] = value;
    this.onStyleChange();
  }

  getPreviewStyles(): any {
    const styles: any = {};
    
    const bgType = this.getBackgroundType();
    if (bgType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (bgType === 'gradient') {
      styles['background'] = `linear-gradient(to right, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (bgType === 'image' && this.getBackgroundImage()) {
      styles['background-image'] = `url(${this.getBackgroundImage()})`;
      styles['background-size'] = 'cover';
      styles['background-position'] = 'center';
      
      if (this.getParallax()) {
        styles['background-attachment'] = 'fixed';
      }
    }

    styles['color'] = this.getTextColor();
    
    // Section height
    const height = this.getSectionHeight();
    if (height) {
      const unit = this.getHeightUnit();
      styles['min-height'] = `${height}${unit}`;
    }
    
    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch(position) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  }
}
